import type { BaseImagingProvider } from '../ViewportDre/modules/imaging/BaseImagingProvider';
import type { Stack, ViewportsConfigurations } from '../ViewportsConfigurations/types';
import type { TStackProviders } from './ViewerContext';
import { getHybridMammographySwapProvidersInWindow } from './useViewerMemoryConfig';
import { collectProvidersInWindowViewports } from './viewportProviders';

/** The two sets {@link collectOnScreenAndHybridSwapProviders} builds. */
export interface OnScreenAndHybridSwapProviders {
  providersOnScreen: ReadonlySet<BaseImagingProvider<Stack>>;
  hybridSwapProviders: ReadonlySet<BaseImagingProvider<Stack>>;
}

/**
 * The providers on screen in any open viewer window, and the hybrid mammography swap counterparts of the stacks on
 * screen in some window (the off-screen 2D/Tomo view one switch away, VX-5535). A swap stack another window shows is in
 * both sets.
 *
 * Shared by ViewerContext's step-aware load priority layout effect (VX-5244), which ranks the first set hanged and the
 * second at HYBRID_SWAP, and by `ViewerContext.getScreenSets` (VX-5537), which the viewer-load ledger reads, so the two
 * cannot drift apart.
 */
export function collectOnScreenAndHybridSwapProviders({
  viewportsConfigurations,
  stackProviders,
  openWindowIds,
  hybridMammographyEnabled,
}: {
  viewportsConfigurations: Nullable<ViewportsConfigurations>;
  stackProviders: TStackProviders;
  openWindowIds: ReadonlyArray<string>;
  hybridMammographyEnabled: boolean;
}): OnScreenAndHybridSwapProviders {
  const providersOnScreen = new Set<BaseImagingProvider<Stack>>();
  const hybridSwapProviders = new Set<BaseImagingProvider<Stack>>();
  for (const openWindowId of openWindowIds) {
    const providersInWindow = collectProvidersInWindowViewports({
      viewportsConfigurations,
      stackProviders,
      viewerId: openWindowId,
    });
    providersInWindow.forEach((provider) => providersOnScreen.add(provider));
    getHybridMammographySwapProvidersInWindow({
      stackProviders,
      viewportsConfigurations,
      hybridMammographyEnabled,
      viewerId: openWindowId,
      stackProvidersInViewports: providersInWindow,
    }).forEach((provider) => hybridSwapProviders.add(provider));
  }
  return { providersOnScreen, hybridSwapProviders };
}
